import { CommonModule } from "@angular/common";
import { FocusMonitor, FocusOrigin } from "@angular/cdk/a11y";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { ConnectedPosition, OverlayModule, ConnectedOverlayPositionChange } from "@angular/cdk/overlay";
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
} from "@angular/forms";
import { Subscription } from "rxjs";

import { DropdownOption } from "./dropdown.model";
import { TranslateModule } from "@ngx-translate/core";
import { isNullOrUndefined } from "src/app/functions/common-functions";

@Component({
  selector: "app-dropdown",
  templateUrl: "./dropdown.component.html",
  styleUrls: ["./dropdown.component.scss"],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, OverlayModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
})
export class DropdownComponent implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() label = "";
  @Input() options: DropdownOption[] = [];
  @Input() placeholder = "";
  @Input() disabled = false;
  @Input() value: string | number = "";
  @Input() required: boolean = false;
  @Input() hideRequiredSign: boolean = false;
  @Input() openOnKeyboardFocus = true;
  @Input() width: string = "100%";
  @Output() selectionChange = new EventEmitter<string>();

  @ViewChild("containerBtn", { static: false })
  containerEl!: ElementRef<HTMLElement>;

  @HostBinding("class.input--focused")
  focused = false;

  @HostBinding("class.dropdown--open")
  get dropdownOpen(): boolean {
    return this.isOpen;
  }

  readonly dropdownPositions: ConnectedPosition[] = [
    {
      originX: "start",
      originY: "bottom",
      overlayX: "start",
      overlayY: "top",
      offsetY: 2,
    },
    {
      originX: "start",
      originY: "top",
      overlayX: "start",
      overlayY: "bottom",
      offsetY: -2,
    },
  ];

  isOpen = false;
  openUp = false;
  menuWidth = 0;
  selected?: DropdownOption;
  activeIndex = -1;

  private static openedDropdown: DropdownComponent | null = null;

  private onTouched = () => {};
  private onChange = (v: any) => {};
  private injector = inject(Injector);
  private readonly focusMonitor = inject(FocusMonitor);
  private ngControl: NgControl | null;
  private focusMonitorSubscription?: Subscription;

  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit() {
    this.ngControl = this.injector.get(NgControl, null, {
      optional: true,
      self: true,
    });

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngAfterViewInit(): void {
    if (!this.containerEl) {
      return;
    }

    this.focusMonitorSubscription = this.focusMonitor
      .monitor(this.containerEl)
      .subscribe((origin) => this.handleFocusOrigin(origin));
  }

  ngOnDestroy(): void {
    this.focusMonitorSubscription?.unsubscribe();

    if (DropdownComponent.openedDropdown === this) {
      DropdownComponent.openedDropdown = null;
    }

    if (this.containerEl) {
      this.focusMonitor.stopMonitoring(this.containerEl);
    }
  }

  toggle(): void {
    if (this.disabled) {
      return;
    }

    if (this.isOpen) {
      this.close();
      return;
    }

    this.open();
  }

  select(option: DropdownOption): void {
    this.applySelection(option);
  }

  writeValue(obj: any): void {
    if (isNullOrUndefined(obj)) {
      this.selected = null;
      this.value = "";
      this.activeIndex = -1;
      return;
    }

    this.value = obj;
    this.selected = this.options?.find(o => o.value === obj) || null;
    this.syncActiveIndexWithSelection();
    this.cd.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngOnChanges(): void {
    if (this.options?.length) {
      this.selected = this.options.find(o => o.value === this.value) || null;
      this.syncActiveIndexWithSelection();
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  close(): void {
    this.isOpen = false;
    this.openUp = false;
    this.syncActiveIndexWithSelection();

    if (DropdownComponent.openedDropdown === this) {
      DropdownComponent.openedDropdown = null;
    }

    this.onTouched();
    this.cd.markForCheck();
  }

  private open(): void {
    if (this.disabled) {
      return;
    }

    if (this.isOpen) {
      DropdownComponent.openedDropdown = this;
      return;
    }

    if (DropdownComponent.openedDropdown && DropdownComponent.openedDropdown !== this) {
      DropdownComponent.openedDropdown.close();
    }

    this.isOpen = true;
    this.syncActiveIndexWithSelection();
    DropdownComponent.openedDropdown = this;

    if (this.containerEl?.nativeElement) {
      this.menuWidth = Math.round(this.containerEl.nativeElement.getBoundingClientRect().width);
    }

    this.cd.markForCheck();
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.disabled || !this.options?.length) {
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!this.isOpen) {
          this.open();
        }
        this.moveSelection(1);
        return;
      case "ArrowUp":
        event.preventDefault();
        if (!this.isOpen) {
          this.open();
        }
        this.moveSelection(-1);
        return;
      case "Enter":
      case " ":
        if (!this.isOpen) {
          event.preventDefault();
          this.open();
          return;
        }

        event.preventDefault();
        this.confirmActiveSelection();
        return;
      case "Tab":
        if (!this.isOpen) {
          return;
        }

        this.confirmActiveSelection();
        return;
      case "Escape":
        if (!this.isOpen) {
          return;
        }

        event.preventDefault();
        this.close();
        return;
      default:
        return;
    }
  }

  private handleFocusOrigin(origin: FocusOrigin): void {
    // Defer the focused flag update to avoid NG0100: FocusMonitor can fire during
    // ngAfterViewInit (e.g. when a MatDialog auto-focuses the first element), which
    // is still inside Angular's change-detection "checked" window. Scheduling via
    // Promise.resolve() moves the assignment to a microtask after CD finishes.
    Promise.resolve().then(() => {
      this.focused = !!origin;
      this.cd.markForCheck();
    });

    if (!origin) {
      if (this.isOpen) {
        this.close();
        return;
      }

      this.onTouched();
      return;
    }

    if (origin === "keyboard" && this.openOnKeyboardFocus && !this.disabled) {
      this.open();
    }
  }

  onOverlayPositionChange(event: ConnectedOverlayPositionChange): void {
    this.openUp = event.connectionPair.overlayY === "bottom";
  }

  private moveSelection(step: 1 | -1): void {
    if (!this.options?.length) {
      return;
    }

    const selectedIndex = this.getSelectedIndex();
    const baseIndex = this.activeIndex >= 0 ? this.activeIndex : selectedIndex;

    let nextIndex = baseIndex;

    if (baseIndex === -1) {
      nextIndex = step > 0 ? 0 : this.options.length - 1;
    } else {
      nextIndex = Math.max(0, Math.min(baseIndex + step, this.options.length - 1));
    }

    if (nextIndex === -1) {
      return;
    }

    this.activeIndex = nextIndex;
    this.cd.markForCheck();
  }

  onOptionMouseDown(event: MouseEvent): void {
    event.preventDefault();
  }

  private applySelection(option: DropdownOption, closeDropdown = true, markTouched = true): void {
    if (this.disabled || !option) {
      return;
    }

    const hasChanged = this.selected?.value !== option.value;

    this.selected = option;
    this.value = option.value;
    this.activeIndex = this.getSelectedIndex();

    if (closeDropdown) {
      this.isOpen = false;
      this.openUp = false;
    }

    if (hasChanged) {
      this.onChange(option.value);
      this.selectionChange.emit(option.value);
    }

    if (markTouched) {
      this.onTouched();
    }

    this.cd.markForCheck();
  }

  private confirmActiveSelection(): void {
    if (!this.isOpen) {
      return;
    }

    if (this.activeIndex < 0 || this.activeIndex >= this.options.length) {
      this.close();
      return;
    }

    this.applySelection(this.options[this.activeIndex]);
  }

  private syncActiveIndexWithSelection(): void {
    this.activeIndex = this.getSelectedIndex();
  }

  private getSelectedIndex(): number {
    if (!this.selected || !this.options?.length) {
      return -1;
    }

    return this.options.findIndex((option) => option.value === this.selected?.value);
  }

  get isRequired(): boolean {
    const validator = this.ngControl?.control?.validator;
    if (!validator) {
      return this.required;
    }

    const result = validator({} as any);
    return result?.required === true;
  }
}
